/**
 * Application Insights telemetry sink (PROD-573 + PROD-593).
 *
 * Real client wiring (PROD-593 INFRA GATE close-out):
 *   1. The runtime config (`/config.json`) carries
 *      `appInsightsConnectionString` (nullable). Templated by
 *      `docker-entrypoint.sh` from K8s env var
 *      `APPLICATIONINSIGHTS_CONNECTION_STRING` sourced from the
 *      `devkeyvault` Secret.
 *   2. `initializeTelemetry` calls `createAppInsightsSink(connStr)` only
 *      when the string is non-null.
 *   3. The sink lazy-resolves the AppInsights client INSIDE `emit` via a
 *      dynamic-import shim so tests can mock cleanly and the bundle does
 *      not pay the SDK cost when Live Mode is never reached.
 *   4. Each emit calls `appInsights.trackEvent({ name, properties })` with
 *      the full FHIR AuditEvent envelope flattened to App Insights
 *      customDimensions. Failures inside the SDK are isolated so the
 *      sink registry's other sinks (console) keep working.
 *
 * HC5: zero em / en dashes.
 * HC9: no test makes a real network call. The SDK is reached only via
 *   `dynamicImport` so a `vi.fn().mockResolvedValue(...)` fully drives
 *   both the client-present and client-absent branches.
 */
import type { TelemetrySink } from './console';
import type { TelemetryEnvelope } from '../events';

interface AppInsightsConfig {
  connectionString: string;
  /**
   * Optional dependency injection of the dynamic importer so tests can
   * drive both the SDK-present and SDK-absent branches without spinning
   * up the real package. Production passes the default importer that
   * resolves `@microsoft/applicationinsights-web`.
   */
  dynamicImport?: DynamicImporter;
}

export type DynamicImporter = (mod: string) => Promise<unknown>;

export const defaultDynamicImport: DynamicImporter = (mod) =>
  import(/* @vite-ignore */ mod);

/**
 * Production-shape client surface. The Microsoft SDK exports a richer
 * surface; this minimal shape is what the sink calls. The wired
 * implementation adapts the SDK's `ApplicationInsights` class to this
 * shape so the sink does not depend on SDK internals.
 */
interface AppInsightsClient {
  trackEvent: (event: {
    name: string;
    properties?: Record<string, unknown>;
  }) => void;
}

/**
 * Build the App Insights sink. Pass the connection string fetched from
 * the K8s-injected runtime config. Returns null if `connectionString` is
 * empty so the caller can skip wiring altogether.
 */
export function createAppInsightsSink(
  config: AppInsightsConfig,
): TelemetrySink | null {
  if (!config.connectionString || config.connectionString.length === 0) {
    return null;
  }

  const importer = config.dynamicImport ?? defaultDynamicImport;
  let warned = false;
  let resolvedClient: AppInsightsClient | null = null;
  let resolutionStarted = false;

  return {
    name: 'app-insights',
    emit(envelope: TelemetryEnvelope) {
      // Lazy-resolve the client on first emit. The SDK is loaded via
      // dynamic import so test runs that never reach Live Mode never
      // pay the bundle cost. The first emit kicks off resolution and
      // queues a microtask; subsequent emits use the resolved client.
      if (!resolutionStarted) {
        resolutionStarted = true;
        void resolveClient(config.connectionString, importer)
          .then((client) => {
            resolvedClient = client;
            if (client === null && !warned) {
              warned = true;
              console.warn(
                '[telemetry/app-insights] @microsoft/applicationinsights-web could not be resolved; ' +
                  'events on this sink are dropped until the dep is installed and the bundle is rebuilt.',
              );
            }
          })
          .catch((err) => {
            if (!warned) {
              warned = true;
              console.warn(
                '[telemetry/app-insights] SDK resolution threw; falling back to no-op',
                err,
              );
            }
          });
      }

      if (resolvedClient === null) {
        // Either the SDK has not finished resolving yet (first frame) or
        // resolution returned null. In either case, drop the event;
        // production's first emits land on console anyway and the
        // dashboard backfills as soon as the SDK is ready.
        return;
      }

      try {
        resolvedClient.trackEvent({
          name: envelope.eventName,
          properties: flattenEnvelope(envelope),
        });
      } catch (err) {
        // Recursion-safe: this sink does NOT call back into emitTelemetry.
        console.warn('[telemetry/app-insights] trackEvent threw', err);
      }
    },
  };
}

/**
 * Flatten the FHIR AuditEvent envelope into a string-keyed map suitable
 * for App Insights customDimensions. Nested objects are JSON-stringified
 * so the dashboard can pivot on top-level fields without losing detail.
 */
function flattenEnvelope(
  envelope: TelemetryEnvelope,
): Record<string, unknown> {
  return {
    id: envelope.id,
    recorded: envelope.recorded,
    eventName: envelope.eventName,
    correlation_id: envelope.correlation_id,
    agent: JSON.stringify(envelope.agent),
    source: JSON.stringify(envelope.source),
    entity: JSON.stringify(envelope.entity),
  };
}

/**
 * Resolve the AppInsights client by lazy-loading the SDK. Returns
 * `null` when the SDK is not bundled or fails to initialize, mirroring
 * the PROD-589 LLM router and PROD-592 metrics client patterns.
 */
async function resolveClient(
  connectionString: string,
  importer: DynamicImporter,
): Promise<AppInsightsClient | null> {
  let mod: unknown;
  try {
    mod = await importer('@microsoft/applicationinsights-web');
  } catch {
    return null;
  }
  interface ApplicationInsightsInstance {
    loadAppInsights: () => void;
    trackEvent: (event: {
      name: string;
      properties?: Record<string, unknown>;
    }) => void;
  }
  type ApplicationInsightsCtor = new (config: {
    config: { connectionString: string };
  }) => ApplicationInsightsInstance;
  const Ctor =
    (mod as { ApplicationInsights?: ApplicationInsightsCtor })
      .ApplicationInsights ??
    (mod as { default?: ApplicationInsightsCtor }).default;
  if (typeof Ctor !== 'function') return null;
  let raw: ApplicationInsightsInstance;
  try {
    raw = new Ctor({ config: { connectionString } });
    raw.loadAppInsights();
  } catch {
    return null;
  }
  return {
    trackEvent: (event) => raw.trackEvent(event),
  };
}
